import ClubOpeningHours from './ClubOpeningHours'
import OsImpersonateAction from "../../OsImpersonateAction"
import ClubInvitationPanel from '../ClubInvitationPanel'
import React, { useContext, useEffect, useRef, useState } from "react"
import CopyContact from "components/Common/CopyContact"
import LocalizedTextBlock from "components/Common/LocalizedTextBlock"
import { anyPending, useTranslationRefresh } from "components/Common/localizedText"
import { get } from "helpers/api_helper"
import { CLUB_TEXT_BLOCKS } from "../clubTexts"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import toastr from "toastr"
import SweetAlert from "react-bootstrap-sweetalert"
import { capitalize, isEmpty } from "lodash"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap"
import moment from "moment"

import defaultProfilePicture from "../../../../assets/images/users/default-profile-picture.png"
import partnerClubSvg from "../../../../assets/images/partner_club.svg"
import "../../../../assets/scss/custom/pages/_clubAccounts.scss"

import Breadcrumbs from "components/Common/Breadcrumb"

import { getClubInfo, getClubOwner, getClubOrganizers } from "../actions"
import { deleteClub, updateStatus } from "../api"
import ProfileLoadError from "../../ProfileLoadError"
import { formatClubLocation, getClubCourtCount } from "../listColumns"
import {
  getClubProfileImage,
  getTermsAcceptance,
  toExternalUrl,
} from "./profileDisplay"

import { store as socketStore } from "../../../../components/Common/Socket/store"

const DetailItem = ({ children, label, wide }) => (
  <div className={`club-profile-detail${wide ? " is-wide" : ""}`}>
    <div className="club-profile-detail-label">{label}</div>
    <div className="club-profile-detail-value">{children || "—"}</div>
  </div>
)

DetailItem.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string.isRequired,
  wide: PropTypes.bool,
}

DetailItem.defaultProps = {
  children: null,
  wide: false,
}

// One of the header's four labelled groups across the card's width.
const HeaderFact = ({ children, label }) => (
  <section className="club-profile-fact">
    <h4 className="club-profile-fact-label">{label}</h4>
    {children}
  </section>
)

HeaderFact.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
}

const FactLine = ({ children, icon, muted }) => (
  <div className={`club-profile-contact-link${muted ? " text-muted" : ""}`}>
    <i className={`bx ${icon}`} aria-hidden="true" />
    <span>{children}</span>
  </div>
)

FactLine.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.string.isRequired,
  muted: PropTypes.bool,
}

FactLine.defaultProps = {
  muted: false,
}

// Leads to a card further down without a hash in the address: a new history entry would drop the state that keeps the
// Clubs list's search for the breadcrumb and Edit club.
const CardLink = ({ children, target }) => (
  <a
    className="club-profile-fact-link"
    href={`#${target}`}
    onClick={event => {
      event.preventDefault()
      document.getElementById(target)?.scrollIntoView?.({ block: "start" })
    }}
  >
    {children}
  </a>
)

CardLink.propTypes = {
  children: PropTypes.node.isRequired,
  target: PropTypes.string.isRequired,
}

const SectionHeading = ({ children, description }) => (
  <div className="club-profile-section-heading">
    <CardTitle className="font-size-15 mb-1">{children}</CardTitle>
    {description && <p className="text-muted mb-0">{description}</p>}
  </div>
)

SectionHeading.propTypes = {
  children: PropTypes.node.isRequired,
  description: PropTypes.string,
}

SectionHeading.defaultProps = {
  description: null,
}

const ContactBlock = ({ email, name, phone, title }) => (
  <div className="club-profile-contact-block">
    <div className="club-profile-contact-title">{title}</div>
    <div className="font-weight-semibold mb-2">{name || "Not set"}</div>
    {email ? (
      <CopyContact key={email} value={email} label="email" icon="bx bx-envelope" />
    ) : (
      <span className="club-profile-contact-link text-muted">
        <i className="bx bx-envelope" aria-hidden="true" />
        Email not set
      </span>
    )}
    {phone ? (
      <CopyContact key={phone} value={phone} label="phone" icon="bx bx-phone" />
    ) : (
      <span className="club-profile-contact-link text-muted">
        <i className="bx bx-phone" aria-hidden="true" />
        Phone not set
      </span>
    )}
  </div>
)

ContactBlock.propTypes = {
  email: PropTypes.string,
  name: PropTypes.string,
  phone: PropTypes.string,
  title: PropTypes.string.isRequired,
}

const ExternalLink = ({ icon, label, value }) => {
  const url = toExternalUrl(value)

  if (!url) return null

  return (
    <a
      className="club-profile-social-link"
      href={url}
      rel="noopener noreferrer"
      target="_blank"
    >
      <i className={`bx ${icon}`} aria-hidden="true" />
      <span>{label}</span>
      <i className="mdi mdi-open-in-new ml-auto" aria-hidden="true" />
    </a>
  )
}

ExternalLink.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
}

const statusColor = status => {
  if (status === "activated" || status === "enabled" || status === "active") {
    return "success"
  }
  if (status === "pending") return "info"
  if (status === "blocked") return "warning"
  return "secondary"
}

const ClubProfile = props => {
  const {
    clubInfo,
    actorId,
    getClubInfo,
    clubOwner,
    getClubOwner,
    clubOrganizers,
    getClubOrganizers,
    loading,
    profileError,
    match: { params },
    settings,
  } = props

  const [blockAlert, setBlockAlert] = useState(false)
  const [deleteAlert, setDeleteAlert] = useState(false)
  const [activateAlert, setActivateAlert] = useState(false)
  const [successAlert, setSuccessAlert] = useState(false)
  const [alertTitle, setAlertTitle] = useState()
  const [alertDescription, setAlertDescription] = useState()
  const [hideOwnerIdAlert, setHideOwnerIdAlert] = useState(false)

  const globalState = useContext(socketStore)
  const { state: { socket: { socketClientId, socket } = {} } = {} } =
    globalState

  useEffect(() => {
    const listenForResponse = async ({ success, message }) => {
      if (!success) {
        switch (message) {
          case "ACCOUNT_ALREADY_EXIST":
            toastr.error(
              "Couldn't create club account. Update club owner email and try again."
            )
            break
          default:
            toastr.error(message)
        }
      } else {
        setHideOwnerIdAlert(true)
      }
    }

    if (socket) {
      socket.removeAllListeners("accounts-response")
      socket.on("accounts-response", listenForResponse)

      return () => {
        socket.removeAllListeners("accounts-response")
      }
    }
  }, [socket, socketClientId])

  useEffect(() => {
    getClubInfo(params.id)
  }, [getClubInfo, params.id])

  // The club's About and Directions (ADR 0013) as the last read gave them: while one is translated, they are read again, apart from the
  // rest of the profile, which the store holds. Before its read the store holds a club without an id (`{}`), and no texts were read yet.
  const [readTexts, setReadTexts] = useState(null)
  useEffect(() => setReadTexts(null), [clubInfo])
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  const textBlocks =
    readTexts && readTexts.id === clubInfo?.id ? readTexts.localizedTexts : clubInfo?.localizedTexts
  useTranslationRefresh(
    anyPending(textBlocks),
    async () => {
      const { data } = await get(`/clubs/${params.id}`)
      if (mounted.current && data?.id) setReadTexts({ id: data.id, localizedTexts: data.localizedTexts })
    },
    clubInfo?.id
  )

  // The store can still hold the club opened before this one until this one is read: that club is neither shown nor read further.
  const isAddressClub = Boolean(clubInfo?.id) && String(clubInfo.id) === String(params.id)

  useEffect(() => {
    if (isAddressClub && clubInfo.ownerId) {
      getClubOwner(clubInfo.ownerId, clubInfo.id)
    }
  }, [getClubOwner, clubInfo, isAddressClub])

  useEffect(() => {
    if (isAddressClub) {
      getClubOrganizers(clubInfo.id)
    }
  }, [clubInfo, getClubOrganizers, isAddressClub])

  const handleBlock = async () => {
    const result = await updateStatus(params.id, "blocked")

    if (result?.success) {
      setBlockAlert(false)
      setSuccessAlert(true)
      setAlertTitle("Blocked")
      setAlertDescription("Club was successfully blocked.")
      getClubInfo(params.id)
    } else {
      toastr.error(result?.message || "Club could not be blocked.")
    }
  }

  const handleDelete = async () => {
    const result = await deleteClub(params.id)

    if (result?.success) {
      setDeleteAlert(false)
      toastr.success("Club was successfully deleted.")
      props.history.push("/accounts/clubs")
    } else {
      toastr.error(result?.message || "Club could not be deleted.")
    }
  }

  const handleActivate = async () => {
    const result = await updateStatus(params.id, "activated")

    if (result?.success) {
      setActivateAlert(false)
      setSuccessAlert(true)
      setAlertTitle("Activated")
      setAlertDescription("Club was successfully activated.")
      getClubInfo(params.id)
    } else {
      toastr.error(result?.message || "Club could not be activated.")
    }
  }

  const termsAcceptance = getTermsAcceptance(
    clubInfo?.osTos,
    settings?.osTosVersion
  )
  const courtCount = getClubCourtCount(clubInfo)
  const organizers = clubOrganizers?.results || []
  const ownerName = [
    clubInfo?.ownerInfo?.firstName,
    clubInfo?.ownerInfo?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
  const contactName = [
    clubInfo?.contactInfo?.firstName,
    clubInfo?.contactInfo?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
  const sports = Array.isArray(clubInfo?.sports) ? clubInfo.sports : []
  const bookingEnabled = Boolean(clubInfo?.bookingSettings?.bookingEnabled)
  const exclusiveOffer = clubInfo?.exclusiveOffer || {}
  const exclusiveOfferExpired =
    exclusiveOffer.endDate && moment(exclusiveOffer.endDate).isBefore(moment())
  const exclusiveOfferStatus = exclusiveOfferExpired
    ? "expired"
    : exclusiveOffer.status || "inactive"
  const hasOnlinePresence = Boolean(
    toExternalUrl(clubInfo?.contactInfo?.website) ||
      toExternalUrl(clubInfo?.facebookUrl) ||
      toExternalUrl(clubInfo?.instagramUrl) ||
      toExternalUrl(clubInfo?.twitterUrl)
  )

  return (
    <React.Fragment>
      <div className="page-content club-profile-page">
        <Container fluid>
          {isAddressClub && !clubInfo.ownerId && !hideOwnerIdAlert && (
            <Alert color="danger">
              This club doesn&apos;t have an owner account.
            </Alert>
          )}

          <Breadcrumbs
            path={{ pathname: "/accounts/clubs", search: props.location?.state?.clubListSearch || "", state: props.location?.state }}
            title="Clubs"
            breadcrumbItem="Club Profile"
          />

          {profileError ? (
            <ProfileLoadError title="Unable to load club profile" profile="club profile" status={profileError.status}
              onRetry={() => getClubInfo(params.id)} />
          ) : loading || !isAddressClub ? (
            <div className="text-center">
              <Spinner className="mt-5 mb-5" color="primary" />
            </div>
          ) : (
            <>
              <Card className="club-profile-hero">
                <CardBody>
                  <div className="club-profile-hero-main club-profile-identity-row">
                    <div className="club-profile-identity">
                      <img
                        src={getClubProfileImage(clubInfo)}
                        alt=""
                        className="club-profile-avatar"
                        onError={event => {
                          event.currentTarget.onerror = null
                          event.currentTarget.src = defaultProfilePicture
                        }}
                      />
                      <div className="min-w-0">
                        <div className="club-profile-eyebrow">
                          Club #{clubInfo.clubId || "—"}
                          {clubInfo.partnerClub && (
                            <img
                              src={partnerClubSvg}
                              alt="Sportya Partner Club"
                              className="club-profile-partner-mark"
                            />
                          )}
                        </div>
                        <h3 className="club-profile-name">
                          {clubInfo.clubName || "Unnamed club"}
                        </h3>
                        <div className="club-profile-badges">
                          <Badge color={statusColor(clubInfo.status)}>
                            {capitalize(clubInfo.status || "unknown")}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="club-profile-actions">
                      <a
                        className="btn btn-primary"
                        href={`${process.env.REACT_APP_WEB_URL}/clubs/${clubInfo.id}`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        View public profile
                        <i className="mdi mdi-open-in-new ml-2" />
                      </a>
                      {!isEmpty(clubOwner) &&
                        clubInfo?.ownerInfo?.email &&
                        clubInfo?.status !== "blocked" && (
                          <OsImpersonateAction account={{ id: clubInfo.ownerId, email: clubInfo.ownerInfo.email }} clubOwner />
                        )}
                      <Link
                        to={{ pathname: `/accounts/clubs/${clubInfo.id}/edit`, state: props.location?.state }}
                        className="btn btn-light"
                      >
                        Edit club
                      </Link>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          className="club-profile-more-button"
                          color="light"
                          aria-label="More club actions"
                        >
                          <i className="mdi mdi-dots-horizontal" />
                        </DropdownToggle>
                        <DropdownMenu right>
                          {clubInfo.status !== "blocked" && (
                            <DropdownItem onClick={() => setBlockAlert(true)}>
                              Block club
                            </DropdownItem>
                          )}
                          {clubInfo.status !== "activated" && (
                            <DropdownItem onClick={() => setActivateAlert(true)}>
                              {clubInfo.status === "blocked"
                                ? "Unblock club"
                                : "Activate club"}
                            </DropdownItem>
                          )}
                          {clubInfo.status !== "deleted" && (
                            <>
                              <DropdownItem divider />
                              <DropdownItem
                                className="text-danger"
                                onClick={() => setDeleteAlert(true)}
                              >
                                Delete club
                              </DropdownItem>
                            </>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  </div>

                  <div className="club-profile-facts">
                    <HeaderFact label="Location">
                      <FactLine icon="bx-briefcase" muted={!clubInfo.businessName}>
                        {clubInfo.businessName || "Business name not set"}
                      </FactLine>
                      <FactLine icon="bx-map">{formatClubLocation(clubInfo)}</FactLine>
                    </HeaderFact>
                    <HeaderFact label="Sports & courts">
                      {sports.length ? (
                        <div className="club-profile-badges">
                          {sports.map(sport => (
                            <Badge color="soft-primary" key={sport}>
                              {capitalize(sport)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <FactLine icon="bx-tennis-ball" muted>Sports not set</FactLine>
                      )}
                      {courtCount === null ? (
                        <FactLine icon="bx-grid-alt" muted>Courts not provided</FactLine>
                      ) : (
                        <FactLine icon="bx-grid-alt">
                          {courtCount} {courtCount === 1 ? "court" : "courts"}
                        </FactLine>
                      )}
                    </HeaderFact>
                    <HeaderFact label="Booking">
                      <div className="club-profile-badges">
                        <Badge color={bookingEnabled ? "soft-success" : "soft-secondary"}>
                          {bookingEnabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                      <CardLink target="club-booking">View booking configuration</CardLink>
                    </HeaderFact>
                    <HeaderFact label="Organizers">
                      <div className="club-profile-fact-value">{organizers.length}</div>
                      <CardLink target="club-organizers">View organizers</CardLink>
                    </HeaderFact>
                  </div>
                </CardBody>
              </Card>

              {clubInfo.ownerId && actorId && <ClubInvitationPanel id={String(clubInfo.ownerId)} actorId={actorId} />}
              <Row>
                <Col lg="7">
                  <Card className="club-profile-card">
                    <CardBody>
                      <SectionHeading description="Core club data and the people responsible for this account.">
                        Club &amp; contacts
                      </SectionHeading>

                      <div className="club-profile-detail-grid">
                        <DetailItem label="Public name">
                          {clubInfo.clubName}
                        </DetailItem>
                        <DetailItem label="Business name">
                          {clubInfo.businessName}
                        </DetailItem>
                        <DetailItem label="Full address" wide>
                          {clubInfo.location?.name || formatClubLocation(clubInfo)}
                        </DetailItem>
                        <DetailItem label="Owner is organizer">
                          {clubOwner?.isOrganizer ? "Yes" : "No"}
                        </DetailItem>
                        <DetailItem label={`Terms of Service v${settings?.osTosVersion || "—"}`}>
                          <span className={`text-${termsAcceptance.color}`}>
                            {termsAcceptance.label}
                          </span>
                        </DetailItem>
                        {clubInfo.createdAt && (
                          <DetailItem label="Account created">
                            {moment(clubInfo.createdAt).format("D MMM YYYY")}
                          </DetailItem>
                        )}
                      </div>

                      <div className="club-profile-divider" />

                      <div className="club-profile-contact-grid">
                        <ContactBlock
                          title="Account owner"
                          name={ownerName}
                          email={clubInfo?.ownerInfo?.email}
                          phone={clubInfo?.ownerInfo?.phone}
                        />
                        <ContactBlock
                          title="Public contact"
                          name={contactName}
                          email={clubInfo?.contactInfo?.email}
                          phone={clubInfo?.contactInfo?.phone}
                        />
                      </div>

                      <div className="club-profile-divider" />
                      <div className="club-profile-contact-title">
                        Online presence
                      </div>
                      {hasOnlinePresence ? (
                        <div className="club-profile-social-grid">
                          <ExternalLink
                            icon="bx-globe"
                            label="Website"
                            value={clubInfo?.contactInfo?.website}
                          />
                          <ExternalLink
                            icon="bxl-facebook"
                            label="Facebook"
                            value={clubInfo?.facebookUrl}
                          />
                          <ExternalLink
                            icon="bxl-instagram"
                            label="Instagram"
                            value={clubInfo?.instagramUrl}
                          />
                          <ExternalLink
                            icon="bxl-twitter"
                            label="Twitter"
                            value={clubInfo?.twitterUrl}
                          />
                        </div>
                      ) : (
                        <div className="club-profile-empty compact">
                          No website or social profiles have been added.
                        </div>
                      )}
                    </CardBody>
                  </Card>

                  <Card className="club-profile-card" id="club-organizers">
                    <CardBody>
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <SectionHeading description="People who can organize events on behalf of this club.">
                          Assigned organizers
                        </SectionHeading>
                        <Badge color="soft-secondary" className="px-2 py-2">
                          {organizers.length}
                        </Badge>
                      </div>

                      {organizers.length ? (
                        <div className="club-profile-organizer-list">
                          {organizers
                            .slice()
                            .sort(organizer =>
                              organizer.id === clubInfo.ownerId ? -1 : 1
                            )
                            .map(organizer => {
                              const organizerInfo = organizer.organizerInfo || {}
                              const organizerName =
                                organizerInfo.clientType === "company"
                                  ? organizerInfo.companyName
                                  : [
                                      organizerInfo.firstName,
                                      organizerInfo.lastName,
                                    ]
                                      .filter(Boolean)
                                      .join(" ")

                              return (
                                <div
                                  className="club-profile-organizer"
                                  key={organizer.id}
                                >
                                  <span className="club-profile-organizer-avatar">
                                    {(organizerName || "?").charAt(0).toUpperCase()}
                                  </span>
                                  <div className="min-w-0">
                                    <Link
                                      className="font-weight-semibold"
                                      to={`/accounts/organizers/${organizer.id}`}
                                    >
                                      {organizerName || "Unnamed organizer"}
                                      {organizer.id === clubInfo.ownerId
                                        ? " (Owner)"
                                        : ""}
                                    </Link>
                                    <div className="text-muted font-size-12 text-truncate">
                                      {organizerInfo.location?.name ||
                                        "Location not set"}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      ) : (
                        <div className="club-profile-empty">
                          <i className="bx bx-user-plus" aria-hidden="true" />
                          <div>No organizers are assigned to this club.</div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>

                <Col lg="5">
                  <Card className="club-profile-card" id="club-booking">
                    <CardBody>
                      <SectionHeading description="Current access and customer-facing booking rules.">
                        Booking configuration
                      </SectionHeading>

                      <div className="club-profile-detail-grid">
                        <DetailItem label="Booking access">
                          <Badge color={statusColor(clubInfo?.bookingAccess)}>
                            {capitalize(clubInfo?.bookingAccess || "disabled")}
                          </Badge>
                        </DetailItem>
                        <DetailItem label="Public booking">
                          {bookingEnabled ? "Enabled" : "Disabled"}
                        </DetailItem>
                        {["enabled", "blocked"].includes(
                          clubInfo?.bookingAccess
                        ) && (
                          <>
                            <DetailItem label="Application fee">
                              {clubInfo?.bookingSettings?.applicationFee ?? "—"}%
                            </DetailItem>
                            <DetailItem label="Advance booking">
                              {clubInfo?.bookingSettings?.bookInAdvance ?? "—"} days
                            </DetailItem>
                            <DetailItem label="Cancellation limit">
                              {clubInfo?.bookingSettings?.minCancellationTime ??
                                "—"} hours
                            </DetailItem>
                            <DetailItem label="Notification emails">
                              {clubInfo?.bookingSettings?.notificationsEmails ||
                                "Not set"}
                            </DetailItem>
                          </>
                        )}
                      </div>

                      <div className="club-profile-divider" />
                      <div className="club-profile-offer-heading">
                        <div>
                          <div className="club-profile-contact-title mb-1">
                            Booking exclusive offer
                          </div>
                          <span className="text-muted font-size-12">
                            Promotional booking terms configured for this club.
                          </span>
                        </div>
                        <Badge color={statusColor(exclusiveOfferStatus)}>
                          {capitalize(exclusiveOfferStatus)}
                        </Badge>
                      </div>

                      {exclusiveOffer.status === "active" && !exclusiveOfferExpired && (
                        <div className="club-profile-offer-details">
                          <DetailItem label="Availability" wide>
                            {moment(exclusiveOffer.startDate).format("D MMM YYYY")} –{" "}
                            {moment(exclusiveOffer.endDate).format("D MMM YYYY")}
                          </DetailItem>
                          {exclusiveOffer?.weekday?.pricePerHour && (
                            <DetailItem label="Weekday" wide>
                              {exclusiveOffer.weekday.pricePerHour} RON/hour ·{" "}
                              {exclusiveOffer.weekday.startDay} {exclusiveOffer.weekday.startHour} –{" "}
                              {exclusiveOffer.weekday.endDay} {exclusiveOffer.weekday.endHour}
                            </DetailItem>
                          )}
                          {exclusiveOffer?.weekend?.pricePerHour && (
                            <DetailItem label="Weekend" wide>
                              {exclusiveOffer.weekend.pricePerHour} RON/hour ·{" "}
                              {exclusiveOffer.weekend.startDay} {exclusiveOffer.weekend.startHour} –{" "}
                              {exclusiveOffer.weekend.endDay} {exclusiveOffer.weekend.endHour}
                            </DetailItem>
                          )}
                        </div>
                      )}
                    </CardBody>
                  </Card>

                  <Card className="club-profile-card">
                    <CardBody>
                      <SectionHeading description="Weekly availability shown to players.">
                        Opening hours
                      </SectionHeading>

                      <ClubOpeningHours openHours={clubInfo?.openHours} />
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {textBlocks && (
                <Card className="club-profile-card">
                  <CardBody>
                    <SectionHeading description="What players read on the club's page, in each language. The texts and their translations are changed in Edit club.">
                      About &amp; directions
                    </SectionHeading>
                    {Object.entries(CLUB_TEXT_BLOCKS).map(([name, { title, fields }], index) => (
                      <LocalizedTextBlock
                        key={name}
                        id={`club-profile-${name}`}
                        className={index ? "mt-3" : ""}
                        title={title}
                        fields={fields}
                        block={textBlocks[name]}
                        editAction="Edit club"
                      />
                    ))}
                  </CardBody>
                </Card>
              )}
            </>
          )}
        </Container>

        {successAlert ? (
          <SweetAlert
            success
            title={alertTitle || "Success"}
            onConfirm={() => setSuccessAlert(false)}
          >
            {alertDescription}
          </SweetAlert>
        ) : null}

        {blockAlert ? (
          <SweetAlert
            title="Block club?"
            warning
            showCancel
            confirmButtonText="Yes, block it!"
            confirmBtnBsStyle="success"
            cancelBtnBsStyle="danger"
            onConfirm={handleBlock}
            onCancel={() => setBlockAlert(false)}
          >
            This action can be reverted by activating the club again.
          </SweetAlert>
        ) : null}

        {deleteAlert ? (
          <SweetAlert
            title="Delete club?"
            warning
            showCancel
            confirmButtonText="Yes, delete it!"
            confirmBtnBsStyle="success"
            cancelBtnBsStyle="danger"
            onConfirm={handleDelete}
            onCancel={() => setDeleteAlert(false)}
          >
            This action cannot be reverted.
          </SweetAlert>
        ) : null}

        {activateAlert ? (
          <SweetAlert
            title="Activate club?"
            warning
            showCancel
            confirmButtonText="Yes, activate it!"
            confirmBtnBsStyle="success"
            cancelBtnBsStyle="danger"
            onConfirm={handleActivate}
            onCancel={() => setActivateAlert(false)}
          >
            The club will regain access to its active features.
          </SweetAlert>
        ) : null}
      </div>
    </React.Fragment>
  )
}

export { ClubProfile }

ClubProfile.propTypes = {
  clubInfo: PropTypes.object,
  getClubInfo: PropTypes.func,
  clubOwner: PropTypes.object,
  getClubOwner: PropTypes.func,
  clubOrganizers: PropTypes.object,
  getClubOrganizers: PropTypes.func,
  history: PropTypes.object,
  loading: PropTypes.bool,
  match: PropTypes.object,
  profileError: PropTypes.object,
  settings: PropTypes.object,
  actorId: PropTypes.string,
}

const mapStateToProps = ({ clubs, session: { userDetails, settings } = {} }) => ({
  loading: clubs.loading,
  profileError: clubs.profileError,
  clubInfo: clubs.clubInfo,
  clubOwner: clubs.clubOwner,
  clubOrganizers: clubs.clubOrganizers,
  actorId: userDetails.id || userDetails._id,
  settings,
})

const mapDispatchToProps = dispatch => ({
  getClubInfo: id => dispatch(getClubInfo(id)),
  getClubOwner: (id, clubId) => dispatch(getClubOwner(id, clubId)),
  getClubOrganizers: id => dispatch(getClubOrganizers(id)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ClubProfile))
